import { Chip, Group, Text } from '@mantine/core';
import { SOURCE_LABELS } from './parameterConfig';

interface SourceFilterProps {
  /** All sources available for the selected parameter */
  availableSources: string[];
  /** Currently selected sources (empty = all) */
  selectedSources: string[];
  /** Called when selection changes */
  onChange: (sources: string[]) => void;
}

/**
 * Source filter chips for Data Editor.
 *
 * Shows chips for each available source. Empty selection means "all sources".
 * Does not render if only one source is available.
 */
export function SourceFilter({
  availableSources,
  selectedSources,
  onChange,
}: SourceFilterProps) {
  // Don't render if only one source - no filtering needed
  if (availableSources.length <= 1) {
    return null;
  }

  const handleChipClick = (source: string) => {
    if (selectedSources.includes(source)) {
      // Remove source from selection
      onChange(selectedSources.filter(s => s !== source));
    } else {
      // Add source to selection
      onChange([...selectedSources, source]);
    }
  };

  const handleAllClick = () => {
    onChange([]);
  };

  const isAllSelected = selectedSources.length === 0;

  return (
    <Group gap="xs" align="center">
      <Text size="sm" fw={500} c="dimmed">
        Source:
      </Text>
      <Chip
        checked={isAllSelected}
        onChange={handleAllClick}
        size="xs"
        variant="light"
      >
        All
      </Chip>
      {availableSources.map(source => (
        <Chip
          key={source}
          checked={selectedSources.includes(source)}
          onChange={() => handleChipClick(source)}
          size="xs"
          variant="light"
        >
          {SOURCE_LABELS[source] || source}
        </Chip>
      ))}
    </Group>
  );
}
